import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconChevronRight, IconPencil } from '@tabler/icons-react';

type OptionItem = string | Record<string, unknown>;

interface AskUserQuestion {
  question: string;
  options?: OptionItem[];
  multi_select?: boolean;
}

interface AskUserProps {
  questions: AskUserQuestion[];
  askId: string;
  onReply: (answer: string) => void;
}

const optionValue = (option: OptionItem): string =>
  typeof option === 'string'
    ? option
    : (option?.label as string) || (option?.text as string) || (option?.value as string) || JSON.stringify(option);

export const AskUser: React.FC<AskUserProps> = ({ questions, askId, onReply }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  // Index-based selection for multi-select
  const [selectedIndexes, setSelectedIndexes] = useState<Record<number, number[]>>({});

  const answeredCount = Object.keys(answers).length;

  const truncate = (text: string, max: number) => (text.length > max ? text.slice(0, max) + '...' : text);

  const isMultiSelect = (q: AskUserQuestion) => q?.multi_select === true;

  const isOptionSelected = (qi: number, oi: number) => (selectedIndexes[qi] || []).includes(oi);

  const advanceToNextUnanswered = (updated: Record<number, string>) => {
    if (questions.length <= 1) return;
    for (let i = 0; i < questions.length; i++) {
      if (!updated[i]) {
        setActiveTab(i);
        return;
      }
    }
  };

  const selectOption = useCallback(
    (qi: number, option: OptionItem, oi: number) => {
      const q = questions[qi];
      const text = optionValue(option);

      if (isMultiSelect(q)) {
        // Toggle index for multi-select
        setSelectedIndexes((prev) => {
          const indexes = prev[qi] || [];
          const nextIndexes = indexes.includes(oi) ? indexes.filter((i) => i !== oi) : [...indexes, oi];
          return { ...prev, [qi]: nextIndexes };
        });
        // Update answers text
        setAnswers((prev) => {
          const indexes = (selectedIndexes[qi] || []).includes(oi)
            ? (selectedIndexes[qi] || []).filter((i) => i !== oi)
            : [...(selectedIndexes[qi] || []), oi];
          const answerText = indexes
            .map((idx) => optionValue((q.options || [])[idx]))
            .filter(Boolean)
            .join(', ');
          if (answerText) {
            return { ...prev, [qi]: answerText };
          } else {
            const next = { ...prev };
            delete next[qi];
            return next;
          }
        });
        return;
      }

      // Single-select
      setSelectedIndexes((prev) => ({ ...prev, [qi]: [oi] }));

      if (questions.length === 1) {
        onReply(`${questions[qi].question}: ${text}`);
      } else {
        setAnswers((prev) => {
          const updated = { ...prev, [qi]: text };
          advanceToNextUnanswered(updated);
          return updated;
        });
      }
    },
    [questions, onReply, selectedIndexes]
  );

  const confirmMultiSelect = useCallback(
    (qi: number) => {
      const text = answers[qi];
      if (!text) return;
      if (questions.length === 1) {
        onReply(`${questions[qi].question}: ${text}`);
      } else {
        advanceToNextUnanswered(answers);
      }
    },
    [questions, answers, onReply]
  );

  const submitCustom = useCallback(
    (qi: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      const text = input.value.trim();
      if (!text) return;
      input.value = '';
      if (questions.length === 1) {
        onReply(`${questions[qi].question}: ${text}`);
      } else {
        setAnswers((prev) => {
          const updated = { ...prev, [qi]: text };
          advanceToNextUnanswered(updated);
          return updated;
        });
      }
    },
    [questions, onReply]
  );

  const submitAll = useCallback(() => {
    const parts: string[] = [];
    for (let i = 0; i < questions.length; i++) {
      if (answers[i]) {
        parts.push(`${questions[i].question}: ${answers[i]}`);
      }
    }
    if (parts.length > 0) {
      onReply(parts.join('\n'));
    }
  }, [questions, answers, onReply]);

  return (
    // 数字人模式下外层 MsgWrap 会向下继承 text-white / text-shadow / letter-spacing，
    // 白字打在白卡上会看不见。卡片是独立表面，这里显式重置，不依赖调用方。
    <div
      id={askId}
      className="my-3 rounded-xl border border-gray-200 bg-white text-gray-900 overflow-hidden"
      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textShadow: 'none', letterSpacing: 'normal' }}
    >
      {/* Tab bar */}
      {questions.length > 1 && (
        <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50" style={{ scrollbarWidth: 'none' }}>
          {questions.map((q, qi) => (
            <button
              key={qi}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 border-none bg-transparent cursor-pointer text-xs whitespace-nowrap shrink-0 relative transition-all duration-150
                ${activeTab === qi ? 'text-blue-600 bg-white' : 'text-gray-500 hover:text-gray-800 hover:bg-white'}
                ${answers[qi] && activeTab !== qi ? 'text-green-600' : ''}
              `}
              onClick={() => setActiveTab(qi)}
            >
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0
                  ${activeTab === qi ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}
                `}
              >
                {answers[qi] ? <IconCheck size={14} /> : qi + 1}
              </span>
              <span className="max-w-[80px] overflow-hidden text-ellipsis">{truncate(q.question, 12)}</span>
              {activeTab === qi && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t" />}
            </button>
          ))}
        </div>
      )}

      {/* Question panels */}
      {questions.map((q, qi) => (
        <div key={qi} style={{ display: activeTab === qi ? 'block' : 'none' }}>
          <div className="px-5 pt-4 pb-3 flex items-start justify-between gap-3">
            <p className="m-0 text-[15px] font-medium leading-relaxed">{q.question}</p>
            {answers[qi] && (
              <span className="shrink-0 px-2.5 py-0.5 rounded-xl bg-green-50 text-green-600 text-xs font-medium">
                {t('agent.ask_user.answered', 'Answered')}
              </span>
            )}
          </div>
          <div>
            {(q.options || []).map((opt, oi) => {
              const text = optionValue(opt);
              const selected = isOptionSelected(qi, oi);
              return (
                <div
                  key={oi}
                  className={`flex items-center gap-3 px-5 py-3 cursor-pointer border-t border-gray-100 transition-colors duration-150 hover:bg-gray-50 group
                    ${selected ? 'bg-blue-50/50' : ''}
                  `}
                  onClick={() => selectOption(qi, opt, oi)}
                >
                  {isMultiSelect(q) ? (
                    <span
                      className={`w-6 h-6 rounded flex items-center justify-center shrink-0 border
                        ${
                          selected
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-transparent group-hover:border-blue-400'
                        }
                      `}
                    >
                      {selected && <IconCheck size={14} />}
                    </span>
                  ) : (
                    <span
                      className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-semibold shrink-0
                        ${
                          selected
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-500 group-hover:bg-blue-600 group-hover:text-white'
                        }
                      `}
                    >
                      {oi + 1}
                    </span>
                  )}
                  <span
                    className={`flex-1 text-sm leading-snug
                      ${selected ? 'text-blue-600 font-medium' : ''}
                    `}
                  >
                    {text}
                  </span>
                  {!isMultiSelect(q) && (
                    <span
                      className={`shrink-0 flex items-center transition-opacity duration-150
                        ${selected ? 'opacity-100 text-blue-600' : 'opacity-0 group-hover:opacity-100 text-gray-400'}
                      `}
                    >
                      <IconChevronRight size={16} />
                    </span>
                  )}
                </div>
              );
            })}
            {/* Multi-select confirm button */}
            {isMultiSelect(q) && (selectedIndexes[qi] || []).length > 0 && (
              <div className="px-5 py-3 border-t border-gray-100">
                <button
                  className="px-4 py-2 border-none rounded-lg bg-blue-600 text-white text-sm font-medium cursor-pointer transition-opacity duration-150 hover:opacity-90"
                  onClick={() => confirmMultiSelect(qi)}
                >
                  {t('agent.ask_user.confirm', 'Confirm')} ({(selectedIndexes[qi] || []).length})
                </button>
              </div>
            )}
            <div className="flex items-center gap-3 px-5 py-3 border-t border-gray-100">
              <span className="w-6 h-6 flex items-center justify-center text-gray-400 shrink-0">
                <IconPencil size={16} />
              </span>
              <input
                className="flex-1 border-none outline-none bg-transparent text-sm p-0 leading-snug"
                placeholder={t('agent.ask_user.other_placeholder', 'Other...')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitCustom(qi, e);
                }}
              />
            </div>
          </div>
        </div>
      ))}

      {/* Footer */}
      {questions.length > 1 && answeredCount > 0 && (
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-[13px] text-gray-500">
            {answeredCount}/{questions.length} {t('agent.ask_user.answered', 'answered')}
          </span>
          <button
            className="px-5 py-2 border-none rounded-lg bg-blue-600 text-white text-sm font-medium cursor-pointer transition-opacity duration-150 hover:opacity-90"
            onClick={submitAll}
          >
            {t('agent.ask_user.submit_all', 'Submit All')}
          </button>
        </div>
      )}
    </div>
  );
};
